import React, { useState, useEffect, useMemo } from "react";
import {
  Upload,
  Trash2,
  Search,
  Image as ImageIcon,
  Grid,
  List,
  Eye,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  FolderOpen,
} from "lucide-react";
import { showToast } from "../utils/toast";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";

const MediaManagement = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const [viewMode, setViewMode] = useState(() =>
    getStoredBusinessType() === "boutique" ? "list" : "grid",
  );
  const [selectedImages, setSelectedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );

  useEffect(() => {
    loadImages();
  }, []);

  useEffect(() => {
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          setBusinessType(extractBusinessType(data));
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
          }
          return;
        }
      } catch (_error) {
        // ignore and fallback to cache
      }
      try {
        const cached = localStorage.getItem("restaurant-info");
        if (cached) {
          const parsed = JSON.parse(cached);
          setBusinessType(extractBusinessType(parsed));
        }
      } catch (_error) {
        // ignore
      }
    };
    loadBusinessType();
  }, []);

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () =>
      setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const loadImages = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/media/images");
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      } else {
        console.error("Erreur lors du chargement des images");
      }
    } catch (error) {
      console.error("❌ Erreur chargement images:", error);
      showToast("error", "Erreur lors du chargement des images");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          showToast("error", `${file.name} n'est pas une image valide`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          showToast("error", `${file.name} est trop volumineux (max 10MB)`);
          continue;
        }

        const formData = new FormData();
        formData.append("image", file);

        const response = await fetch("/api/upload/product-image", {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          await response.json();
          showToast("success", `${file.name} téléversé avec succès`);
        } else {
          const error = await response.json();
          showToast("error", `Erreur téléversement ${file.name}: ${error.error}`);
        }
      }

      await loadImages();
    } catch (error) {
      console.error("❌ Erreur upload:", error);
      showToast("error", "Erreur lors du téléversement");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteImage = async (imagePath, { skipConfirm = false } = {}) => {
    if (!skipConfirm) {
      const ok = window.confirm(
        "Êtes-vous sûr de vouloir supprimer cette image ?",
      );
      if (!ok) return;
    }

    try {
      const response = await fetch("/api/media/delete", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ imagePath }),
      });

      if (response.ok) {
        showToast("success", "Image supprimée avec succès");
        setSelectedImages((prev) => prev.filter((path) => path !== imagePath));
      } else {
        const error = await response.json();
        showToast("error", `Erreur suppression: ${error.error}`);
      }
    } catch (error) {
      console.error("❌ Erreur suppression:", error);
      showToast("error", "Erreur lors de la suppression");
    } finally {
      await loadImages();
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.length === 0) return;
    const ok = window.confirm(
      `Êtes-vous sûr de vouloir supprimer ${selectedImages.length} image(s) ?`,
    );
    if (!ok) return;

    try {
      for (const imagePath of selectedImages) {
        await handleDeleteImage(imagePath, { skipConfirm: true });
      }
      setSelectedImages([]);
    } catch (error) {
      console.error("❌ Erreur suppression multiple:", error);
    }
  };

  const copyImageUrl = (imagePath) => {
    const fullUrl = `${window.location.origin}${imagePath}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedUrl(imagePath);
      showToast("success", "URL copiée dans le presse-papiers");
      setTimeout(() => setCopiedUrl(null), 2000);
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getImageCategory = (imagePath = "") => {
    if (imagePath.includes("/products/")) return "products";
    if (imagePath.includes("/login/") || imagePath.includes("login"))
      return "login";
    return "other";
  };

  const productImages = useMemo(
    () => images.filter((image) => getImageCategory(image.path) === "products"),
    [images],
  );

  const filteredImages = useMemo(() => {
    return productImages.filter((image) => {
      const haystack = `${image.name} ${image.path}`.toLowerCase();
      return haystack.includes(searchTerm.toLowerCase());
    });
  }, [productImages, searchTerm]);

  const ImageCard = ({ image }) => {
    const isSelected = selectedImages.includes(image.path);
    const isCopied = copiedUrl === image.path;

    return (
      <div
        className={`relative rounded-3xl border transition-all duration-200 ${
          isDarkMode
            ? "bg-white/5 border-white/10 shadow-2xl shadow-black/40"
            : "bg-white border-slate-200 shadow-lg"
        } ${isSelected ? "ring-2 ring-amber-400/60" : ""}`}
      >
        <div className="absolute top-3 left-3 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              setSelectedImages((prev) =>
                e.target.checked
                  ? [...new Set([...prev, image.path])]
                  : prev.filter((path) => path !== image.path),
              );
            }}
            className="w-4 h-4 text-amber-500 bg-white/50 border-white/30 rounded focus:ring-amber-500"
          />
        </div>

        <div
          className="relative h-48 bg-slate-900/5 rounded-t-3xl overflow-hidden cursor-pointer group"
          onClick={() => setPreviewImage(image)}
        >
          <img
            src={image.url}
            alt={image.name}
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
            <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>

        <div className="p-4 space-y-3">
          <h3
            className={`font-semibold truncate ${
              isDarkMode ? "text-white" : "text-slate-900"
            }`}
          >
            {image.name}
          </h3>
          <div
            className={`space-y-1 text-sm ${
              isDarkMode ? "text-slate-400" : "text-slate-500"
            }`}
          >
            <div className="flex items-center justify-between">
              <span>Taille</span>
              <span className="font-medium">
                {formatFileSize(image.size || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Dimensions</span>
              <span className="font-medium">{image.dimensions || "N/A"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Ajouté</span>
              <span className="font-medium">
                {new Date(image.uploadDate || Date.now()).toLocaleDateString(
                  "fr-FR",
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-white/10">
            <button
              onClick={() => copyImageUrl(image.path)}
              className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300"
            >
              {isCopied ? <Check size={14} /> : <Copy size={14} />}
              {isCopied ? "Copié" : "Copier URL"}
            </button>
            <div className="flex gap-1">
              <button
                onClick={() => window.open(image.url, "_blank")}
                className={`p-2 rounded-lg ${
                  isDarkMode
                    ? "text-slate-300 hover:text-white hover:bg-white/10"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
                title="Ouvrir dans un nouvel onglet"
              >
                <ExternalLink size={16} />
              </button>
              <button
                onClick={() => handleDeleteImage(image.path)}
                className="p-2 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg"
                title="Supprimer"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ImageListItem = ({ image }) => {
    const isSelected = selectedImages.includes(image.path);
    const isCopied = copiedUrl === image.path;
    return (
      <div
        className={`flex items-center p-4 rounded-2xl border transition-all duration-200 ${
          isDarkMode
            ? "bg-white/5 border-white/10"
            : "bg-white border-slate-200"
        } ${isSelected ? "ring-2 ring-amber-400/60" : ""}`}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            setSelectedImages((prev) =>
              e.target.checked
                ? [...new Set([...prev, image.path])]
                : prev.filter((path) => path !== image.path),
            );
          }}
          className="w-4 h-4 text-amber-500 border-white/30 rounded focus:ring-amber-500 mr-4"
        />
        <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-900/5 mr-4">
          <img
            src={image.url}
            alt={image.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold truncate">{image.name}</h4>
          <p className="text-xs text-slate-500">{image.path}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">
            {formatFileSize(image.size || 0)}
          </span>
          <button
            onClick={() => copyImageUrl(image.path)}
            className="text-amber-400 hover:text-amber-300"
          >
            {isCopied ? <Check size={16} /> : <Copy size={16} />}
          </button>
          <button
            onClick={() => handleDeleteImage(image.path)}
            className="text-rose-400 hover:text-rose-300"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    );
  };

  const totalSize = productImages.reduce(
    (sum, img) => sum + (img.size || 0),
    0,
  );

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#050b18] via-[#0c182f] to-[#050b18] text-slate-100"
    : "bg-slate-50 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-100 shadow-xl shadow-slate-200/60";

  return (
    <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
      <div className="max-w-7xl mx-auto space-y-6">
        <header className={`${cardClasses} rounded-3xl p-6 space-y-4`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-amber-400/15 p-3 text-amber-300">
                <ImageIcon size={28} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-amber-400">
                  Médiathèque produits
                </p>
                <h1 className="text-3xl font-semibold tracking-tight">
                  Vos visuels disponibles
                </h1>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-slate-400" : "text-slate-500"
                  }`}
                >
                  {productImages.length} média(s) • {selectedImages.length} sélection(s)
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <label className="rounded-2xl bg-gradient-to-r from-emerald-400 to-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg hover:shadow-emerald-500/30 cursor-pointer flex items-center gap-2">
                <Upload size={18} />
                <span>{isUploading ? "Téléversement..." : "Téléverser"}</span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
              </label>
              {selectedImages.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="rounded-2xl bg-rose-500/90 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:bg-rose-500/80 flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Supprimer ({selectedImages.length})
                </button>
              )}
              <button
                onClick={loadImages}
                className="rounded-2xl border border-white/10 px-3 py-2 text-sm font-semibold hover:border-amber-400/60 flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Actualiser
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            {[
              { label: "Images produits", value: productImages.length },
              { label: "Sélection", value: selectedImages.length },
              { label: "Taille totale", value: formatFileSize(totalSize) },
              { label: "Mise à jour", value: new Date().toLocaleDateString("fr-FR") },
            ].map((stat) => (
              <div key={stat.label} className={`${cardClasses} rounded-3xl p-4`}>
                <p className={`text-xs uppercase tracking-[0.3em] ${
                  isDarkMode ? "text-slate-400" : "text-slate-500"
                }`}>
                  {stat.label}
                </p>
                <p className="text-2xl font-semibold mt-2">{stat.value}</p>
              </div>
            ))}
          </div>
        </header>

        <section className={`${cardClasses} rounded-3xl p-6 space-y-4`}>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Rechercher par nom ou chemin..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full rounded-2xl border px-11 py-3 text-sm ${
                  isDarkMode
                    ? "bg-white/5 border-white/15 text-slate-100 placeholder-white/40"
                    : "bg-white border-slate-200 text-slate-900"
                }`}
              />
            </div>
            <div className="flex bg-white/5 border border-white/10 rounded-2xl p-1">
              {["grid", "list"].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-4 py-2 rounded-2xl text-sm font-semibold flex items-center gap-2 ${
                    viewMode === mode
                      ? "bg-amber-400 text-slate-900 shadow-lg"
                      : isDarkMode
                        ? "text-slate-300"
                        : "text-slate-500"
                  }`}
                >
                  {mode === "grid" ? <Grid size={16} /> : <List size={16} />}
                  {mode === "grid" ? "Grille" : "Liste"}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-flex h-12 w-12 animate-spin rounded-full border-4 border-amber-400 border-b-transparent"></div>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="w-14 h-14 mx-auto mb-4 text-slate-400" />
              <p className="text-lg font-semibold">Aucune image produit</p>
              <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                Ajoutez des visuels pour vos fiches produits.
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid gap-4 lg:grid-cols-3">
              {filteredImages.map((image) => (
                <ImageCard key={image.path} image={image} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredImages.map((image) => (
                <ImageListItem key={image.path} image={image} />
              ))}
            </div>
          )}
        </section>

        {previewImage && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-6"
            onClick={() => setPreviewImage(null)}
          >
            <div
              className={`${cardClasses} rounded-3xl p-4 max-w-3xl w-full`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-amber-400">
                    Aperçu
                  </p>
                  <h3 className="text-lg font-semibold">{previewImage.name}</h3>
                </div>
                <button
                  onClick={() => setPreviewImage(null)}
                  className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="rounded-2xl overflow-hidden bg-slate-900/5">
                <img
                  src={previewImage.url}
                  alt={previewImage.name}
                  className="w-full h-auto object-contain max-h-[70vh]"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaManagement;
